<template lang="pug">
.wrap
  .questions
    transition-group(name='el-fade-in')
      .question(v-for='(field, fieldIndex) in fields' :key='fieldIndex + 0')
        .question-label
          icon(:name='field.icon' color='#999' :style='{ marginRight: "10px" }')
          | {{ field.label }}
        .props
          component(:is='getFieldEditor(field)' :field.sync='field' :disabled='disabled')

        .question-actions(v-if='!disabled')
          .question-action(@click='moveField(fieldIndex, "before")')
            icon(name='chevron-up' color='#999')
          .question-action(@click='moveField(fieldIndex, "after")')
            icon(name='chevron-down' color='#999')
          .question-action(@click='deleteField(fieldIndex)')
            icon(name='trash-o' color='#999')

  .question-placeholder(@click.stop='toggleFieldPicker' v-if='!disabled')
    .question-placeholder-label(v-if='!showFieldPicker')
      | + Add a new question

    .field-picker(v-else)
      .question-placeholder-cancel(@click.stop='toggleFieldPicker')
        icon(name='times' color='#999')

      .field-widgets
        .field-widget(v-for='field in fieldOptions' :key='field.type' @click.stop='addField(field)')
          icon.field-icon(:name='field.icon' color='#999')
          .field-label {{ field.label }}

</template>

<script>
import _ from "lodash";
import uuidv4 from "uuid/v4";
import ShortText from "./Fields/ShortText";
import Rating from "./Fields/Rating";
import MultipleChoice from "./Fields/MultipleChoice";
import YesNo from "./Fields/YesNo";

const fieldSchemas = [
  {
    type: "short_text",
    ref: "",
    title: "",
    label: "Short text",
    icon: "terminal",
    properties: {
      description: { label: "Description", type: "text", value: "" }
    }
  },
  {
    type: "rating",
    ref: "",
    title: "",
    label: "Rating",
    icon: "star-half-o",
    properties: {
      description: { label: "Description", type: "text", value: "" },
      steps: { label: "Steps", type: "number", value: 5 },
      shape: {
        label: "Shape",
        type: "choice",
        options: [{ label: "Star", value: "star" }],
        value: "star"
      }
    }
  },
  {
    type: "yes_no",
    ref: "",
    title: "",
    label: "Yes / No",
    icon: "dot-circle-o",
    properties: {
      description: { value: "" }
    }
  },
  {
    type: "multiple_choice",
    ref: "",
    title: "",
    label: "Multiple choice",
    icon: "list-ul",
    properties: {
      description: { value: "" },
      randomize: { value: false },
      allowMultipleSelection: { value: false },
      allowOtherChoice: { value: false },
      verticalAlignment: { value: false },
      choices: {
        options: [{ value: "" }]
      }
    }
  }
];

const fieldEditors = {
  short_text: ShortText,
  multiple_choice: MultipleChoice,
  yes_no: YesNo,
  rating: Rating
};

export default {
  inject: ["mainPane"],
  model: {
    prop: "fields",
    event: "change"
  },
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    fieldOptions() {
      return fieldSchemas;
    }
  },
  data() {
    return {
      showFieldPicker: false
    };
  },
  methods: {
    getFieldEditor(field) {
      return fieldEditors[field.type];
    },
    toggleFieldPicker() {
      this.showFieldPicker = !this.showFieldPicker;
    },
    addField(field) {
      this.fields.push({ ..._.cloneDeep(field), ref: uuidv4() });
      this.toggleFieldPicker();
      this.mainPane.scroll({ to: "bottom" });
    },
    deleteField(fieldIndex) {
      this.fields.splice(fieldIndex, 1);
    },
    moveField(fieldIndex, dir) {
      const to = dir === "before" ? fieldIndex - 1 : fieldIndex + 1;
      this.fields.splice(to, 0, this.fields.splice(fieldIndex, 1)[0]);
    }
  }
};
</script>

<style scoped lang="scss">
.question-placeholder {
  display: flex;
  padding: 5px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  background: white;
  border: 1px dashed #ddd;
  border-radius: 2px;
  color: #999;
  position: relative;
  cursor: pointer;
  transition: all 200ms ease-in-out;

  &:hover {
    transform: scale(1.02);
    border-color: #555;
  }
}

.question {
  background: white;
  border: 1px solid #ddd;
  border-top: 3px solid #ddd;
  border-radius: 2px;
  padding: 10px;
  margin-bottom: 10px;
  position: relative;
}

.question-label {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  background: #eee;
  border-radius: 0 0 2px 0;
  display: inline-block;
  padding: 4px 8px;
  position: relative;
  display: inline-flex;
  align-items: center;
  top: -10px;
  left: -10px;
}

.question-placeholder-label {
  cursor: pointer;
  padding: 20px 0;
}

.question-placeholder-cancel {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
}

.field-widgets {
  display: flex;
  align-items: center;
  justify-content: center;
}

.field-widget {
  cursor: pointer;
  flex: 0 0 120px;
  height: 90px;
  margin: 5px;
  padding: 5px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  border-radius: 2px;
  transition: all 200ms ease-in-out;

  &:hover {
    transform: scale(1.05);
    border-color: #444;
  }
}

.field-label {
  text-align: center;
}

.field-icon {
  margin-bottom: 8px;
}

.field-label {
  font-size: 14px;
  font-weight: bold;
}

.question-actions {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  vertical-align: middle;
}

.question-action {
  margin-right: 10px;
  cursor: pointer;
  transition: all 200ms ease-in-out;

  &:hover {
    transform: scale(1.2);
    color: #333;
  }

  &:last-child {
    margin-right: 0;
  }
}
</style>
